import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Gift, Copy, WhatsappLogo, UsersThree, CalendarCheck, Trophy } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";

const SITE = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, "");

export default function Refer() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/referrals/me").then((r) => setData(r.data)); }, []);

  const link = data ? `${SITE}/register?ref=${data.referral_code}` : "";
  const msg = `Book farmhouses, resorts & halls around Hyderabad on Avinya Bookings! Sign up with my link: ${link}`;
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Referral link copied"); };
  const copyCode = () => { navigator.clipboard.writeText(data.referral_code); toast.success("Code copied"); };
  const wa = () => window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");

  return (
    <div className="px-4 pt-5" data-testid="refer-page">
      <Seo title="Refer & Earn" path="/refer" />
      <h1 className="font-heading text-2xl font-semibold text-stone-900">Refer & Earn</h1>
      <p className="mt-1 text-sm text-stone-500">Invite friends to Avinya Bookings. Track your referrals below.</p>

      {!data ? null : (
        <>
          <div className="mt-4 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary to-green-900 p-6 text-white">
            <Gift size={28} weight="duotone" />
            <p className="mt-3 text-xs uppercase tracking-widest text-white/70">Your referral code</p>
            <div className="mt-1 flex items-center gap-3">
              <p data-testid="referral-code" className="font-heading text-3xl font-bold tracking-wide">{data.referral_code}</p>
              <button onClick={copyCode} data-testid="copy-code" className="rounded-full bg-white/15 p-2 hover:bg-white/25"><Copy size={16} /></button>
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button onClick={copy} data-testid="copy-link" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white py-3 text-sm font-semibold text-primary hover:bg-white/90"><Copy size={16} /> Copy link</button>
              <button onClick={wa} data-testid="share-whatsapp" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-green-500 py-3 text-sm font-semibold text-white hover:bg-green-600"><WhatsappLogo size={18} weight="fill" /> Share on WhatsApp</button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            {[[UsersThree, data.counts.registered, "Joined"], [CalendarCheck, data.counts.first_booking, "Booked"], [Trophy, data.counts.reward_eligible, "Rewards"]].map(([Icon, v, l], i) => (
              <div key={i} className="rounded-2xl border border-stone-200 bg-white p-4 text-center">
                <Icon size={22} weight="duotone" className="mx-auto text-primary" />
                <p className="mt-1 font-heading text-2xl font-bold text-stone-900">{v}</p>
                <p className="text-xs text-stone-500">{l}</p>
              </div>
            ))}
          </div>

          {data.reward.enabled ? (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="reward-info">
              <p className="font-semibold">Reward: ₹{Number(data.reward.amount).toLocaleString("en-IN")} per successful referral</p>
              {data.reward.note ? <p className="mt-1 text-amber-700">{data.reward.note}</p> : null}
            </div>
          ) : (
            <p className="mt-4 text-center text-xs text-stone-400">Referral rewards are coming soon — keep inviting to build up your referrals!</p>
          )}

          <div className="mt-6">
            <p className="mb-2 font-heading font-semibold text-stone-800">Your referrals</p>
            {data.referrals.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-stone-200 bg-white p-6 text-center text-sm text-stone-400">No referrals yet. Share your link to get started!</p>
            ) : (
              <div className="space-y-2">
                {data.referrals.map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-4 py-3">
                    <span className="text-sm font-medium text-stone-800">{r.referred_name}</span>
                    <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">{r.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
